(() => {
  const ROOT_SELECTOR = '[data-amada-home]';
  const MANAGER_KEY = '__amadaHomeRedesignManager';
  const previousManager = window[MANAGER_KEY];

  if (previousManager?.destroy) previousManager.destroy();

  const roots = new Set();
  const globalController = new AbortController();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function rootsWithin(scope = document) {
    const matches = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) matches.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => matches.push(root));
    return matches;
  }

  function setupHero(root, signal, cleanups) {
    const carousel = root.querySelector('[data-home-hero-carousel]');
    const slides = Array.from(root.querySelectorAll('[data-home-hero-slide]'));
    const controls = Array.from(root.querySelectorAll('[data-home-hero-control]'));
    const previous = root.querySelector('[data-home-hero-prev]');
    const next = root.querySelector('[data-home-hero-next]');
    if (!carousel || slides.length === 0) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let pointerStart = null;
    let suppressClick = false;
    let autoplayTimer = null;
    let autoplayPaused = false;
    const autoplayEnabled = carousel.dataset.homeHeroAutoplay === 'true' && slides.length > 1;
    const autoplayInterval = Math.min(10000, Math.max(3000, Number(carousel.dataset.homeHeroInterval) || 6000));

    const clearAutoplay = () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || autoplayPaused || reducedMotion.matches || document.hidden) return;
      autoplayTimer = window.setTimeout(() => {
        activate(activeIndex + 1, { resetAutoplay: false });
        scheduleAutoplay();
      }, autoplayInterval);
    };

    function activate(requestedIndex, { focusControl = false, resetAutoplay = true } = {}) {
      const nextIndex = (requestedIndex + slides.length) % slides.length;
      activeIndex = nextIndex;

      slides.forEach((slide, index) => {
        const active = index === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      controls.forEach((control, index) => {
        const active = index === nextIndex;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && focusControl) control.focus({ preventScroll: true });
      });

      if (resetAutoplay) scheduleAutoplay();
    }

    controls.forEach((control) => {
      control.addEventListener('click', () => activate(Number(control.dataset.slideIndex)), { signal });
    });
    previous?.addEventListener('click', () => activate(activeIndex - 1), { signal });
    next?.addEventListener('click', () => activate(activeIndex + 1), { signal });

    carousel.addEventListener('keydown', (event) => {
      if (event.target.matches('input, textarea, select')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        activate(activeIndex - 1, { focusControl: controls.includes(event.target) });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        activate(activeIndex + 1, { focusControl: controls.includes(event.target) });
      }
    }, { signal });

    carousel.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || slides.length < 2) return;
      if (event.target.closest('button, a')) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      suppressClick = false;
    }, { signal });

    const finishPointer = (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      const threshold = Math.min(90, Math.max(45, carousel.clientWidth * 0.055));
      if (Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
        suppressClick = true;
        activate(activeIndex + (deltaX < 0 ? 1 : -1));
        window.setTimeout(() => { suppressClick = false; }, 0);
      }
      pointerStart = null;
    };

    carousel.addEventListener('pointerup', finishPointer, { signal });
    carousel.addEventListener('pointercancel', () => { pointerStart = null; }, { signal });
    carousel.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, { capture: true, signal });

    document.addEventListener('visibilitychange', scheduleAutoplay, { signal });
    reducedMotion.addEventListener?.('change', scheduleAutoplay, { signal });

    root._amadaHomeHeroActivate = activate;
    activate(activeIndex, { resetAutoplay: false });
    scheduleAutoplay();
    cleanups.push(clearAutoplay);
  }

  function setupFinalCarousel(root, signal, cleanups) {
    const carousel = root.querySelector('[data-home-final-carousel]');
    const slides = Array.from(root.querySelectorAll('[data-home-final-slide]'));
    const previous = root.querySelector('[data-home-final-prev]');
    const next = root.querySelector('[data-home-final-next]');
    if (!carousel || slides.length === 0) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let autoplayTimer = null;
    let autoplayPaused = false;
    let pointerStart = null;
    /* 稿上移动端这块是「两组图文纵向排开」，不是轮播（运营 2026-08-14 确认按稿走）；
       PC 端才是带左右箭头的两页轮播。所以这里按断点分叉：
       ≤990 把所有页都设为可见并停掉自动播放，>990 才走单页轮播。 */
    const stackedMobile = window.matchMedia('(max-width: 990px)');
    const autoplayEnabled = carousel.dataset.homeFinalAutoplay === 'true' && slides.length > 1;
    const autoplayInterval = Math.min(10000, Math.max(3000, Number(carousel.dataset.homeFinalInterval) || 6000));

    const clearAutoplay = () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (stackedMobile.matches || !autoplayEnabled || autoplayPaused || reducedMotion.matches || document.hidden) return;
      autoplayTimer = window.setTimeout(() => {
        activate(activeIndex + 1, { resetAutoplay: false });
        scheduleAutoplay();
      }, autoplayInterval);
    };

    function activate(requestedIndex, { resetAutoplay = true } = {}) {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      if (stackedMobile.matches) {
        // 纵向堆叠时两页都要可见、可读屏、可 Tab——别留 inert/aria-hidden 在身上。
        slides.forEach((slide) => {
          slide.classList.add('is-active');
          slide.setAttribute('aria-hidden', 'false');
          slide.inert = false;
        });
        clearAutoplay();
        return;
      }
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      if (resetAutoplay) scheduleAutoplay();
    }

    const syncLayout = () => activate(activeIndex, { resetAutoplay: false });

    previous?.addEventListener('click', () => {
      if (!stackedMobile.matches) activate(activeIndex - 1);
    }, { signal });
    next?.addEventListener('click', () => {
      if (!stackedMobile.matches) activate(activeIndex + 1);
    }, { signal });
    carousel.addEventListener('keydown', (event) => {
      if (stackedMobile.matches) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      activate(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    carousel.addEventListener('pointerdown', (event) => {
      if (stackedMobile.matches) return;
      if (!event.isPrimary || slides.length < 2) return;
      if (event.target.closest('button, a')) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { signal });
    carousel.addEventListener('pointerup', (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      if (Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY)) activate(activeIndex + (deltaX < 0 ? 1 : -1));
      pointerStart = null;
    }, { signal });
    carousel.addEventListener('pointercancel', () => { pointerStart = null; }, { signal });
    document.addEventListener('visibilitychange', scheduleAutoplay, { signal });
    reducedMotion.addEventListener?.('change', scheduleAutoplay, { signal });
    // 桌面→移动→桌面来回切时要重排，否则从桌面缩下来会留着「只显示第 N 页」的状态。
    stackedMobile.addEventListener?.('change', syncLayout, { signal });

    root._amadaHomeFinalActivate = activate;
    activate(activeIndex, { resetAutoplay: false });
    scheduleAutoplay();
    cleanups.push(clearAutoplay);
  }

  /* 横滑轨道（Lived-in 画廊 / 移动端商品卡）通用逻辑：
     箭头翻页 + 自动轮播 + 到头回绕，按「一屏能放几张」计算步长。 */
  function setupTrackCarousel(root, cfg, signal, cleanups) {
    const track = root.querySelector(cfg.track);
    const previous = root.querySelector(cfg.prev);
    const next = root.querySelector(cfg.next);
    if (!track || !previous || !next) return;

    const cards = Array.from(track.querySelectorAll(cfg.card));
    if (cards.length === 0) return;
    const autoplayEnabled = cfg.autoplay !== false && track.dataset.homeAutoplay === 'true';
    const autoplayInterval = Math.min(10000, Math.max(3000, Number(track.dataset.homeInterval) || 6000));
    let autoplayTimer = null;
    let autoplayPaused = false;
    let activeIndex = 0;
    let programmaticScroll = false;

    const clearAutoplay = () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const cardStep = () => cards[0]?.getBoundingClientRect().width || track.clientWidth;
    const visibleCount = () => Math.max(1, Math.floor((track.clientWidth + 1) / Math.max(1, cardStep())));
    const maxIndex = () => Math.max(0, cards.length - visibleCount());
    const canMove = () => maxIndex() > 0;
    /* edgeDisabled：到头就把箭头置灰、不回绕。商品卡稿上左箭头画的就是灰色禁用态；
       Lived-in 画廊沿用桌面的回绕行为，两者别混。 */
    const updateControls = () => {
      const movable = canMove();
      previous.disabled = !movable || (cfg.edgeDisabled === true && activeIndex <= 0);
      next.disabled = !movable || (cfg.edgeDisabled === true && activeIndex >= maxIndex());
    };

    const goTo = (requestedIndex, { resetAutoplay = true } = {}) => {
      const last = maxIndex();
      activeIndex = cfg.edgeDisabled === true
        ? Math.min(Math.max(requestedIndex, 0), last)
        : requestedIndex > last ? 0 : requestedIndex < 0 ? last : requestedIndex;
      programmaticScroll = true;
      track.scrollTo({ left: activeIndex * cardStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      window.setTimeout(() => { programmaticScroll = false; }, reducedMotion.matches ? 0 : 550);
      if (resetAutoplay) scheduleAutoplay();
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || autoplayPaused || reducedMotion.matches || document.hidden || !canMove()) return;
      autoplayTimer = window.setTimeout(() => {
        goTo(activeIndex + 1, { resetAutoplay: false });
        scheduleAutoplay();
      }, autoplayInterval);
    };

    previous.addEventListener('click', () => goTo(activeIndex - 1), { signal });
    next.addEventListener('click', () => goTo(activeIndex + 1), { signal });
    track.addEventListener('scroll', () => {
      // 先按滚动位置回推当前页，再更新箭头——顺序反了，手滑到头箭头不会置灰。
      if (!programmaticScroll) activeIndex = Math.min(maxIndex(), Math.max(0, Math.round(track.scrollLeft / cardStep())));
      updateControls();
    }, { signal });
    document.addEventListener('visibilitychange', scheduleAutoplay, { signal });
    reducedMotion.addEventListener?.('change', scheduleAutoplay, { signal });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => {
        activeIndex = Math.min(activeIndex, maxIndex());
        track.scrollTo({ left: activeIndex * cardStep(), behavior: 'auto' });
        updateControls();
        scheduleAutoplay();
      });
      observer.observe(track);
      cleanups.push(() => observer.disconnect());
    } else {
      window.addEventListener('resize', updateControls, { passive: true, signal });
    }

    requestAnimationFrame(updateControls);
    requestAnimationFrame(scheduleAutoplay);
    cleanups.push(clearAutoplay);
  }

  /* 2026-10-05：商品卡「ADD TO SHOPPING BAG」原来是原生表单 POST，点完整页跳到 /cart；
     全站其它加购（PDP / 快速预览）都是留在当前页、右侧弹出购物袋抽屉，这里改成同一体验
     （用户拍板）。加购成功后在 document 上派发 cart:updated，让 assets/amada-auto-discount.js
     按商品自动挂券码（运营 10.5 #11）。没有 fetch 时不拦截，表单照常提交、功能不丢。 */
  function setupProductForms(root, signal) {
    root.addEventListener('submit', (event) => {
      const form = event.target.closest?.('[data-home-product-form]');
      if (!form || typeof window.fetch !== 'function') return;
      const button = form.querySelector('button[type="submit"]');
      const id = Number(form.elements.id?.value);
      if (!id || button?.disabled) return;
      event.preventDefault();

      const quantity = Math.max(1, Number(form.elements.quantity?.value) || 1);
      const base = window.Shopify?.routes?.root || '/';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }

      window.fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(() => {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: null, source: 'amada-home-card' } }));
          if (window.AmadaCartDrawer && typeof window.AmadaCartDrawer.open === 'function') {
            return window.AmadaCartDrawer.open({ justAdded: true });
          }
          window.location.href = base + 'cart';
          return null;
        })
        .catch(() => {
          /* 加购失败要可感知（库存不足等），不跳页 */
          if (!button) return;
          button.classList.add('is-error');
          window.setTimeout(() => button.classList.remove('is-error'), 2000);
        })
        .finally(() => {
          if (!button) return;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
    }, { signal });
  }

  function teardownHome(root) {
    root._amadaHomeCleanup?.();
  }

  function setupHome(root) {
    if (!root || root.dataset.amadaHomeReady === 'true') return;
    const controller = new AbortController();
    const cleanups = [];

    setupHero(root, controller.signal, cleanups);
    setupFinalCarousel(root, controller.signal, cleanups);
    setupProductForms(root, controller.signal);
    setupTrackCarousel(
      root,
      {
        track: '[data-home-social-track]',
        prev: '[data-home-social-prev]',
        next: '[data-home-social-next]',
        card: '.amada-home__social-card',
      },
      controller.signal,
      cleanups
    );
    /* 移动端商品卡横滑：稿只画了箭头、没有自动轮播，所以这里显式关掉 autoplay。 */
    setupTrackCarousel(
      root,
      {
        track: '[data-home-product-track]',
        prev: '[data-home-product-prev]',
        next: '[data-home-product-next]',
        card: '.amada-home__product-card',
        autoplay: false,
        edgeDisabled: true,
      },
      controller.signal,
      cleanups
    );

    root.dataset.amadaHomeReady = 'true';
    root._amadaHomeCleanup = () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      delete root.dataset.amadaHomeReady;
      delete root._amadaHomeCleanup;
      delete root._amadaHomeHeroActivate;
      delete root._amadaHomeFinalActivate;
      roots.delete(root);
    };
    roots.add(root);
  }

  function setupAll(scope = document) {
    rootsWithin(scope).forEach(setupHome);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll(), { once: true, signal: globalController.signal });
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target), { signal: globalController.signal });
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach(teardownHome);
  }, { signal: globalController.signal });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-home-hero-slide], [data-home-final-slide]');
    const root = slide?.closest(ROOT_SELECTOR);
    if (slide?.matches('[data-home-hero-slide]') && root?._amadaHomeHeroActivate) {
      root._amadaHomeHeroActivate(Number(slide.dataset.slideIndex));
    } else if (slide?.matches('[data-home-final-slide]') && root?._amadaHomeFinalActivate) {
      root._amadaHomeFinalActivate(Number(slide.dataset.slideIndex));
    }
  }, { signal: globalController.signal });

  window[MANAGER_KEY] = {
    destroy() {
      globalController.abort();
      Array.from(roots).forEach(teardownHome);
    }
  };
})();
